import React, { ChangeEvent, forwardRef, HtmlHTMLAttributes, InputHTMLAttributes, memo, useId, useState } from "react";
import classNames from "classnames";
import { Tag, TagClose } from "../../../Tag";

import styles from "./styles.module.scss";

export type TagsContainerProps = Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "ref"> & {
  value?: string[] | null
  dividers?: string[]
  onChange?: (value: string[] | null, e?: ChangeEvent<HTMLInputElement> | null) => void,
  tagClassName?: string
  checkInputData?: (value: string | undefined) => Promise<boolean> | boolean
  containerProps?: HtmlHTMLAttributes<HTMLDivElement>
  tagCloseClassName?: string
  tagsInputClassName?: string
  tagsContainerClassName?: string
}

export const TagsContainer = memo(forwardRef<HTMLDivElement, TagsContainerProps>((
  {
    id,
    name,
    value,
    dividers = [ "Enter" ],
    onChange,
    className,
    tagClassName,
    containerProps,
    checkInputData = () => true,
    tagCloseClassName,
    tagsInputClassName,
    tagsContainerClassName,
    ...props
  },
  ref,
) => {
  const reactId = useId();
  const realId = id || reactId;
  const [ inputValue, setInputValue ] = useState<string>();
  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (dividers?.includes(e.key)) {
      const isValidInputData = await checkInputData?.(inputValue);
      if (isValidInputData) {
        onChange?.([ ...(value || []), inputValue || "" ]);
        setInputValue("");
      }
    }
  };

  const handleTagRemoveClick = (tagIndex: number) => {
    onChange?.((value || []).filter((_, i) => i !== tagIndex));
  };

  return (
    <div
      className={classNames(
        styles.root,
        className,
      )}
      {...containerProps}
      ref={ref}
    >
      {value?.map((tag, index) => (
        <Tag
          key={index + tag}
          children={tag}
          className={classNames(tagClassName)}
          endAdornment={
            <TagClose
              tagSize="md"
              className={classNames(tagCloseClassName)}
              onClick={() => handleTagRemoveClick(index)}
            />
          }
        />
      ))}
      <input
        {...props}
        id={realId}
        type="text"
        value={inputValue || ""}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        className={classNames(
          styles.tagsInput,
          tagsInputClassName,
        )}
      />
    </div>
  );
}));